"use client";

/**
 * Unified login card: one centered card with Customer (default) and Business modes.
 * Uses a horizontal slide + crossfade (250ms) for mode switch to avoid layout jump
 * and keep a single form (inputs stay focused). Pill switch with sliding indicator
 * for clear, minimal feedback.
 */
import { useState, useCallback, useTransition, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { getApiUrl, setCustomerToken } from "@/lib/api";
import { setToken } from "@/lib/auth";
import { fsElement } from "@/lib/fs";

const DURATION_MS = 250;
const SERVER_UNAVAILABLE = "The server is temporarily unavailable. Please try again in a moment.";
const HEALTH_POLL_MS = 15_000;

type Mode = "customer" | "business";
type LoginJson = { error?: string; code?: string; token?: string; user?: { passwordResetRequired?: boolean; emailChangeRequired?: boolean } };
type ApiReachability = "checking" | "ok" | "down";

function isGatewayError(status: number) {
  return status === 502 || status === 503 || status === 504;
}

async function probeApiHealth(): Promise<boolean> {
  const apiUrl = getApiUrl();
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 5_000);
  try {
    const res = await fetch(`${apiUrl}/api/health`, {
      method: "GET",
      credentials: "include",
      cache: "no-store",
      signal: controller.signal,
    });
    return res.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timeout);
  }
}

const content: Record<
  Mode,
  { title: string; description: string; submitLabel: string; icon: string }
> = {
  customer: {
    title: "Sign in as a customer",
    description: "Use your account to save your details and manage bookings.",
    submitLabel: "Sign in",
    icon: "👤",
  },
  business: {
    title: "Sign in as a business",
    description: "Access your dashboard, bookings, and business settings.",
    submitLabel: "Log in",
    icon: "🏢",
  },
};

export function UnifiedLoginCard() {
  const router = useRouter();
  const searchParams = useSearchParams();

  const callbackUrl = searchParams.get("callbackUrl") ?? "";
  const returnTo = searchParams.get("returnTo") ?? "";
  const isBusinessCallback = callbackUrl.startsWith("/admin") || callbackUrl.startsWith("/app");
  const initialMode = (searchParams.get("mode") === "business" || (searchParams.get("mode") !== "customer" && isBusinessCallback) ? "business" : "customer") as Mode;

  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [apiStatus, setApiStatus] = useState<ApiReachability>("checking");
  const [isPending, startTransition] = useTransition();

  const isCustomer = mode === "customer";
  const isAdminLogin = callbackUrl.startsWith("/admin");
  const businessCallbackUrl = callbackUrl || "/app/dashboard";
  // Use callbackUrl for customer only when it's a customer-facing path (/customer or /book e.g. checkout)
  const isCustomerCallback = callbackUrl && (callbackUrl.startsWith("/customer") || callbackUrl.startsWith("/book"));
  const customerCallbackUrl = (isCustomerCallback ? callbackUrl : null) || returnTo || "/customer";
  const apiDown = apiStatus === "down";

  useEffect(() => {
    let cancelled = false;

    async function check() {
      const ok = await probeApiHealth();
      if (!cancelled) setApiStatus(ok ? "ok" : "down");
    }

    void check();
    const id = window.setInterval(() => {
      void check();
    }, HEALTH_POLL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  const switchMode = useCallback((next: Mode) => {
    if (next === mode) return;
    setError("");
    setMode(next);
  }, [mode]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (apiDown) {
      setError(SERVER_UNAVAILABLE);
      return;
    }

    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();
    const apiUrl = getApiUrl();

    const endpoint = isCustomer ? "/api/auth/customer/login" : "/api/auth/login";
    let res: Response;
    try {
      res = await fetch(`${apiUrl}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: trimmedEmail, password: trimmedPassword }),
      });
    } catch {
      setApiStatus("down");
      setError(SERVER_UNAVAILABLE);
      return;
    }
    const data = (await res.json().catch(() => ({}))) as LoginJson;
    if (!res.ok) {
      if (isGatewayError(res.status)) {
        setApiStatus("down");
        setError(SERVER_UNAVAILABLE);
        return;
      }
      if (res.status === 429) {
        const retryAfter = res.headers.get("Retry-After");
        const wait = retryAfter ? `${Math.ceil(Number(retryAfter) / 60)} minute(s)` : "a few minutes";
        setError(data.error ?? `Too many attempts. Please wait ${wait} and try again.`);
        return;
      }
      if (data.code === "USE_BUSINESS_LOGIN") {
        setMode("business");
        setError(data.error ?? "This is a business account. Please use the Business login tab.");
        return;
      }
      setError(data.error ?? "Invalid email or password");
      return;
    }
    if (isCustomer) {
      if (data.token) setCustomerToken(data.token);
      startTransition(() => {
        router.push(customerCallbackUrl);
        router.refresh();
      });
      return;
    }
    if (data.token) setToken(data.token);
    if (data.user?.passwordResetRequired || data.user?.emailChangeRequired) {
      window.location.href = "/app/set-password";
      return;
    }
    window.location.href = businessCallbackUrl;
  }

  const c = content[mode];

  return (
    <div className="w-full max-w-2xl rounded-xl border border-border bg-card shadow-lg overflow-hidden flex flex-col sm:flex-row">
      {/* Left: form content */}
      <div className="flex-1 p-6 min-w-0">
      {apiDown && (
        <div
          className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-sm text-amber-950 dark:text-amber-100"
          role="status"
        >
          <p className="font-medium">Sign-in is temporarily unavailable</p>
          <p className="mt-0.5 text-amber-900/80 dark:text-amber-100/80">
            We can’t reach the Book4 servers right now. We’ll keep checking, or tap Try again.
          </p>
          <button
            type="button"
            className="mt-2 text-sm font-medium text-amber-950 underline underline-offset-2 hover:no-underline dark:text-amber-100"
            onClick={() => {
              setApiStatus("checking");
              void probeApiHealth().then((ok) => setApiStatus(ok ? "ok" : "down"));
            }}
          >
            Try again
          </button>
        </div>
      )}

      {/* Mode switch: pill with sliding background (250ms) */}
      <div className="mb-6">
        <div
          className="relative inline-grid grid-cols-2 rounded-full border border-border bg-muted/60 p-0.5 text-sm"
          role="tablist"
          aria-label="Login type"
        >
          {/* Sliding pill indicator — 250ms ease for minimal, responsive feel */}
          <span
            className="pointer-events-none absolute top-0.5 z-0 rounded-full bg-background shadow-sm transition-[left] duration-[250ms] ease-out"
            style={{
              left: isCustomer ? "0.125rem" : "calc(50% + 0.125rem)",
              width: "calc(50% - 0.25rem)",
              height: "calc(100% - 0.25rem)",
            }}
            aria-hidden
          />
          <button
            type="button"
            role="tab"
            aria-selected={isCustomer}
            aria-controls="login-form"
            onClick={() => switchMode("customer")}
            className={`relative z-10 rounded-full px-4 py-2 font-medium transition-colors duration-200 hover:text-foreground ${isCustomer ? "text-foreground" : "text-muted-foreground"}`}
            {...fsElement("Login Mode Customer Tab", { id: "login-mode-customer", section: "login" })}
          >
            Customer
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={!isCustomer}
            aria-controls="login-form"
            onClick={() => switchMode("business")}
            className={`relative z-10 rounded-full px-4 py-2 font-medium transition-colors duration-200 hover:text-foreground ${!isCustomer ? "text-foreground" : "text-muted-foreground"}`}
            {...fsElement("Login Mode Business Tab", { id: "login-mode-business", section: "login" })}
          >
            Business
          </button>
        </div>
      </div>

      {/* Animated header: crossfade + short horizontal slide (250ms) to signal context change without layout jump */}
      <div className="relative mb-4 min-h-[3.5rem] overflow-hidden">
        <div
          key={mode}
          className="animate-login-header"
          style={{
            animationDuration: `${DURATION_MS}ms`,
          }}
        >
          <p className="text-muted-foreground mb-0.5 text-base" aria-hidden>
            {c.icon}
          </p>
          <h1 className="text-xl font-semibold text-foreground">{c.title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{c.description}</p>
        </div>
      </div>

      <form id="login-form" onSubmit={handleSubmit} className="space-y-4" aria-label={c.title}>
        <div>
          <label htmlFor="login-email" className="block text-sm font-medium mb-1">
            Email
          </label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
            placeholder="you@example.com"
            required
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label htmlFor="login-password" className="block text-sm font-medium">
              Password
            </label>
            {isCustomer ? (
              <Link
                href="/auth/customer/forgot-password"
                className="text-xs text-muted-foreground hover:text-primary transition-colors"
                {...fsElement("Forgot Password Link", { id: "login-forgot-password-customer", section: "login" })}
              >
                Forgot password?
              </Link>
            ) : (
              <Link
                href="/auth/forgot-password"
                className="text-xs text-muted-foreground hover:text-primary transition-colors"
                {...fsElement("Forgot Password Link", { id: "login-forgot-password-business", section: "login" })}
              >
                Forgot password?
              </Link>
            )}
          </div>
          <input
            id="login-password"
            type="password"
            autoComplete={isCustomer ? "current-password" : "current-password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="fs-mask w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
            required
          />
        </div>
        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={isPending || apiDown}
          className="w-full rounded-lg bg-primary py-2.5 text-sm font-semibold text-primary-foreground shadow-sm hover:opacity-95 transition-opacity focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-70"
          {...fsElement(isCustomer ? "Customer Sign In Button" : "Business Log In Button", {
            id: isCustomer ? "login-submit-customer" : "login-submit-business",
            section: "login",
          })}
        >
          {isPending ? "Signing in…" : apiDown ? "Unavailable" : c.submitLabel}
        </button>
      </form>

      <p className="mt-4 text-center text-sm text-muted-foreground">
        {isCustomer ? (
          <>
            Don&apos;t have an account?{" "}
            <Link
              href={
                customerCallbackUrl !== "/customer"
                  ? `/auth/customer/signup?returnTo=${encodeURIComponent(customerCallbackUrl)}`
                  : "/auth/customer/signup"
              }
              className="text-primary hover:underline"
              {...fsElement("Create Customer Account Link", { id: "login-create-customer", section: "login" })}
            >
              Create one
            </Link>
          </>
        ) : (
          !isAdminLogin && (
            <>
              Don&apos;t have an account?{" "}
              <Link
                href="/auth/signup"
                className="text-primary hover:underline"
                {...fsElement("Business Sign Up Link", { id: "login-signup-business", section: "login" })}
              >
                Sign up
              </Link>
            </>
          )
        )}
      </p>

      {/* CTA to switch mode if user landed on wrong one */}
      <p className="mt-3 text-center text-xs text-muted-foreground">
        {isCustomer ? (
          <>
            Have a business account?{" "}
            <button
              type="button"
              onClick={() => switchMode("business")}
              className="text-primary hover:underline font-medium"
              {...fsElement("Switch to Business Login Button", { id: "login-switch-business", section: "login" })}
            >
              Switch to Business login
            </button>
          </>
        ) : (
          <>
            Booking as a customer?{" "}
            <button
              type="button"
              onClick={() => switchMode("customer")}
              className="text-primary hover:underline font-medium"
              {...fsElement("Switch to Customer Login Button", { id: "login-switch-customer", section: "login" })}
            >
              Switch to Customer login
            </button>
          </>
        )}
      </p>
      </div>

      {/* Right: decorative image */}
      <div className="hidden sm:block relative w-full sm:w-64 shrink-0 border-t sm:border-t-0 sm:border-l border-border overflow-hidden">
        <Image
          src="/barber-cutting.png"
          alt=""
          fill
          className="object-cover"
          sizes="(min-width: 640px) 512px, 0px"
          quality={100}
        />
      </div>
    </div>
  );
}
